import { useEffect, useRef, useState } from "react";
import { reportsApi } from "../../services/apiClient";
import { formatDateTimeFr } from "../../shared/dateFormat";
import { COLORS, RADIUS } from "../../shared/theme";

/** Texte toast après génération (API renvoie item.downloadFileName). */
export function reportGenerateSuccessToastMessage(data, fallbackOk = "Rapport généré") {
  const msg = data?.message || fallbackOk;
  const raw = data?.item?.downloadFileName;
  const fn = typeof raw === "string" ? raw.trim() : "";
  if (fn) {
    return `${msg} — Fichier : ${fn}`;
  }
  return msg;
}

export function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || "download.bin";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export function statusPill(status) {
  if (status === "sealed") {
    return { bg: "#dcfce7", color: "#166534", label: "Scellé TSA" };
  }
  if (status === "pending_signatures") {
    return { bg: "#fef3c7", color: "#92400e", label: "En attente de signatures" };
  }
  return { bg: "#e2e8f0", color: "#334155", label: "Brouillon" };
}

export function signatureMatchesCurrentUser(signature, currentUser) {
  if (!signature || !currentUser) return false;
  if (signature.expectedUserId && Number(signature.expectedUserId) === Number(currentUser.id)) return true;
  if (signature.expectedEmail && currentUser.email) {
    return signature.expectedEmail.toLowerCase() === currentUser.email.toLowerCase();
  }
  return false;
}

function SignaturePad({ onDataUrlChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);

  const notify = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!hasInkRef.current) {
      onDataUrlChange?.(null);
      return;
    }
    onDataUrlChange?.(canvas.toDataURL("image/png"));
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    hasInkRef.current = false;
    onDataUrlChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init canvas once; setter stable
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = "touches" in e && e.touches[0] ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e && e.touches[0] ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hasInkRef.current = true;
  };

  const end = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    drawingRef.current = false;
    notify();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasInkRef.current = false;
    onDataUrlChange?.(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={560}
        height={160}
        style={{
          width: "100%",
          height: 140,
          border: "1px solid #cbd5e1",
          borderRadius: 10,
          touchAction: "none",
          cursor: "crosshair",
          display: "block",
          background: "#fff",
        }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <button
        type="button"
        onClick={clear}
        style={{
          marginTop: 8,
          fontSize: 12,
          fontWeight: 700,
          color: "#475569",
          background: "#f1f5f9",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          padding: "6px 12px",
          cursor: "pointer",
        }}
      >
        Effacer le tracé
      </button>
    </div>
  );
}

function SignatureThumb({ reportId, signatureId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const blob = await reportsApi.signatureImageBlob(reportId, signatureId);
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(objectUrl);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [reportId, signatureId]);
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      style={{ maxHeight: 52, marginTop: 8, border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff" }}
    />
  );
}

export function ProofModal({ details, reportId, currentUser, onClose, onRefresh, onToast }) {
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);

  useEffect(() => {
    setSignatureDataUrl(null);
  }, [reportId, details]);

  if (!details) return null;

  const proof = details.proof || {};
  const item = details.item || {};
  const pill = statusPill(item.status);

  const handleArtifactDownload = async (kind) => {
    const { blob, filename } = await reportsApi.downloadArtifact(reportId, kind);
    saveBlob(blob, filename);
  };

  const handleSign = async () => {
    if (!signatureDataUrl) {
      onToast?.("error", "Tracez votre signature dans la zone avant de valider.");
      return;
    }
    const base64 = signatureDataUrl.split(",")[1];
    if (!base64) {
      onToast?.("error", "Signature invalide, réessayez.");
      return;
    }
    try {
      const data = await reportsApi.sign(reportId, { signatureImageBase64: base64 });
      onToast?.("success", data?.message || "Signature enregistrée");
      await onRefresh?.();
    } catch (error) {
      onToast?.("error", error.message || "Impossible de signer le rapport");
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "min(980px, 100%)", maxHeight: "90vh", overflow: "auto", background: "#fff", borderRadius: 18, boxShadow: "0 24px 60px rgba(15,23,42,0.28)" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>preuve horodatée</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{proof.title || "Rapport"}</div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 22, cursor: "pointer", color: "#64748b" }}>×</button>
        </div>

        <div style={{ padding: 22, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: RADIUS.lg, padding: 16 }}>
            <div style={{ fontWeight: 800, marginBottom: 12, color: COLORS.primary }}>Résumé</div>
            <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: "8px 10px", fontSize: 13 }}>
              <div style={{ color: "#64748b", fontWeight: 700 }}>Organisation</div><div>{proof.organization || "—"}</div>
              <div style={{ color: "#64748b", fontWeight: 700 }}>Type</div><div>{proof.report_type || "—"}</div>
              <div style={{ color: "#64748b", fontWeight: 700 }}>Version</div><div>{proof.version_label || "—"}</div>
              <div style={{ color: "#64748b", fontWeight: 700 }}>Workflow</div><div><span style={{ padding: "4px 10px", borderRadius: 999, background: pill.bg, color: pill.color, fontSize: 12, fontWeight: 800 }}>{pill.label}</span></div>
              <div style={{ color: "#64748b", fontWeight: 700 }}>Preuve TSA</div><div>{proof.validation_status || "—"}</div>
              <div style={{ color: "#64748b", fontWeight: 700 }}>Horodaté le</div><div>{proof.tsa_generated_at || "—"}</div>
              <div style={{ color: "#64748b", fontWeight: 700 }}>Hash SHA-256</div><div style={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: 12 }}>{proof.sha256 || "—"}</div>
              <div style={{ color: "#64748b", fontWeight: 700 }}>Serial TSA</div><div>{proof.serial || "—"}</div>
            </div>
            {item.canCurrentUserSign && (
              <div id="report-sign-pad-anchor" style={{ marginTop: 16, padding: 12, borderRadius: 12, border: "1px solid #bfdbfe", background: "#f8fafc" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 8 }}>Signature manuscrite (souris ou doigt)</div>
                <SignaturePad key={reportId} onDataUrlChange={setSignatureDataUrl} />
                <button
                  onClick={handleSign}
                  style={{
                    marginTop: 12,
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "none",
                    background: "#2563eb",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  Signer électroniquement avec mon compte
                </button>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
              {(proof.artifact_downloads || []).map((artifact) => (
                <button
                  key={artifact.kind}
                  onClick={() => handleArtifactDownload(artifact.kind)}
                  style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#f8fafc", cursor: "pointer", fontWeight: 700, fontSize: 12 }}
                >
                  Télécharger {artifact.kind}
                </button>
              ))}
            </div>
          </div>

          <div style={{ border: "1px solid #e5e7eb", borderRadius: RADIUS.lg, padding: 16 }}>
            <div style={{ fontWeight: 800, marginBottom: 12, color: COLORS.primary }}>Signatures attendues</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(proof.signatures || []).map((signature) => {
                const isMine = signatureMatchesCurrentUser(signature, currentUser);
                return (
                  <div key={signature.id || signature.role} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: "10px 12px", background: isMine ? "#eff6ff" : "#f8fafc" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{signature.role}</div>
                        <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>
                          {signature.expectedName || "Compte à associer"}{signature.expectedFunction ? ` - ${signature.expectedFunction}` : ""}
                        </div>
                        {signature.expectedEmail && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{signature.expectedEmail}</div>}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: signature.status === "signed" ? "#166534" : "#92400e" }}>
                          {signature.status === "signed" ? "Signé" : "En attente"}
                        </div>
                        {signature.signedAt && <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{formatDateTimeFr(signature.signedAt)}</div>}
                        {signature.signedByEmail && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{signature.signedByEmail}</div>}
                        {signature.signatureHash && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, fontFamily: "monospace" }}>{signature.signatureHash.slice(0, 12)}</div>}
                        {signature.status === "signed" && signature.hasSignatureImage && signature.id && (
                          <SignatureThumb reportId={reportId} signatureId={signature.id} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ gridColumn: "1 / -1", border: "1px solid #e5e7eb", borderRadius: RADIUS.lg, padding: 16 }}>
            <div style={{ fontWeight: 800, marginBottom: 12, color: COLORS.primary }}>
              {item.status === "sealed" ? "Journal de vérification" : "Etat de la preuve"}
            </div>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 260, overflow: "auto", background: "#0f172a", color: "#e2e8f0", borderRadius: 12, padding: 14, fontSize: 12 }}>
              {item.status === "sealed"
                ? (proof.verification_output || "Aucune sortie de vérification disponible.")
                : "Le rapport ne sera scellé par TSA qu'après collecte de toutes les signatures requises."}
            </pre>
          </div>

          {item.status === "sealed" && (
            <div style={{ gridColumn: "1 / -1", border: "1px solid #e5e7eb", borderRadius: RADIUS.lg, padding: 16 }}>
              <div style={{ fontWeight: 800, marginBottom: 12, color: COLORS.primary }}>Réponse TSA</div>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 220, overflow: "auto", background: "#0f172a", color: "#e2e8f0", borderRadius: 12, padding: 14, fontSize: 12 }}>
                {proof.reply_output || "Aucune sortie TSA disponible."}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Pages d'URL reconnues pour les liens profonds (email, favoris). */
export const REPORTS_LIST_PAGE_KEYS = ["reports-list", "reports"];

export function isReportsListDeepLinkPage(pageParam) {
  return REPORTS_LIST_PAGE_KEYS.includes(pageParam);
}
